<template>
  <b-container class="square-terminal-create-device-code">
    <b-row
      v-if="loading"
      class="mt-3"
    >
      <b-col>
        <loading-spinner>
          <div slot="title">
            Please wait while loading available locations
          </div>
        </loading-spinner>
      </b-col>
    </b-row>

    <b-row
      v-if="!loading"
      class="mt-3"
    >
      <b-col>
        <b-form @submit.prevent="createDeviceCode">
          <b-input-group
            prepend="Device Name"
            class="mb-3"
          >
            <b-form-input
              id="device-name"
              v-model="deviceCode.name"
              :disabled="processing"
              placeholder="e.g., Main Counter Terminal"
              required
            />
          </b-input-group>
          <small class="form-text text-muted mb-3">
            Enter a name to identify this terminal device
          </small>

          <b-input-group
            prepend="Location"
            class="mb-3"
          >
            <b-form-select
              id="device-location"
              v-model="deviceCode.locationId"
              :options="locationOptions"
              :disabled="processing"
              required
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  Please select a location
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-input-group>
          <small class="form-text text-muted mb-4">
            Select the Square location where this device will be used
          </small>

          <div class="modal-footer-section">
            <div class="buttons">
              <b-button
                variant="secondary"
                :disabled="processing"
                class="btn-cancel"
                @click="cancelCreateDeviceCode"
              >
                Cancel
              </b-button>
              <b-button
                :disabled="processing || !isFormValid"
                variant="primary"
                type="submit"
                class="btn-create"
              >
                Create Device Code
                <b-spinner
                  v-if="processing"
                  small
                  class="ml-2"
                />
              </b-button>
            </div>
          </div>
        </b-form>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
export default {
  name: 'square-terminal-create-device-code',
  data: function() {
    return {
      loading: false,
      processing: false,
      deviceCode: {
        name: '',
        locationId: null
      }
    }
  },
  computed: {
    locations: function() {
      return this.$store.state.square.locations || []
    },
    locationOptions: function() {
      return this.locations.map(location => ({
        value: location.id,
        text: `${location.name}${location.description ? ` - ${location.description}` : ''}`,
        disabled: location.status !== 'ACTIVE'
      }))
    },
    isFormValid: function() {
      return this.deviceCode.name.trim().length > 0 &&
             this.deviceCode.locationId !== null &&
             this.deviceCode.locationId.trim().length > 0
    },
    selectedLocation: function() {
      if (!this.deviceCode.locationId) return null
      return this.locations.find(loc => loc.id === this.deviceCode.locationId)
    }
  },
  mounted: function() {
    // Locations should already be loaded by the parent component
    // but we can ensure they're available
    if (this.locations.length === 0) {
      this.loading = true
      this.$store.dispatch('square/loadLocations', { force: true })
        .then(() => {
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.$emit('error', {
            device: { name: this.deviceCode.name },
            error: err
          })
        })
    }
  },
  methods: {
    cancelCreateDeviceCode: function() {
      this.$emit('cancel')
    },

    createDeviceCode: function() {
      if (!this.isFormValid) return

      this.processing = true

      const payload = {
        locationId: this.deviceCode.locationId,
        deviceName: this.deviceCode.name.trim()
      }

      this.$store.dispatch('square/createDeviceCode', payload)
        .then(response => {
          // Don't set processing to false here - keep button disabled until modal closes

          // Create a device object that matches the expected format
          const deviceData = {
            id: response.device_code?.id || response.id,
            name: this.deviceCode.name.trim(),
            label: this.deviceCode.name.trim(),
            location_id: this.deviceCode.locationId,
            code: response.device_code?.code || response.code,
            status: response.device_code?.status || 'UNPAIRED',
            ...response.device_code,
            ...response
          }

          // Reload device codes list to reflect the new device code
          this.$store.dispatch('square/loadDeviceCodesList', { force: true })
            .then(() => {
              this.$emit('success', deviceData)
              // Note: processing will be reset when component is destroyed/reset
            })
            .catch(() => {
              // Even if reload fails, we still succeeded in creating
              this.$emit('success', deviceData)
              // Note: processing will be reset when component is destroyed/reset
            })
        })
        .catch(err => {
          this.processing = false
          this.$emit('error', {
            device: {
              name: this.deviceCode.name,
              locationId: this.deviceCode.locationId
            },
            error: err
          })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.square-terminal-create-device-code {
  padding: 1.5rem;

  // Style the Bootstrap Vue input group prepend labels
  ::v-deep .input-group-prepend .input-group-text {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-right: none;
    color: #495057;
    font-weight: 500;
    font-size: 0.875rem;
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0.75rem 1rem;
  }

  // Style the input fields to connect with the prepend labels
  ::v-deep .input-group .form-control,
  ::v-deep .input-group .custom-select {
    border-left: none;
    padding-left: 1rem; // Add padding to the left of the select field content
    border: 1px solid #dee2e6; // Consistent border color
    border-left: none; // Remove left border to connect with prepend
    border-radius: 0 0.375rem 0.375rem 0; // Rounded right corners only

    &:focus {
      border-left: none;
      border-color: #80bdff; // Consistent focus border color
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  // Specific styling for Bootstrap Vue select component
  ::v-deep .input-group .form-select,
  ::v-deep .input-group select {
    border: 1px solid #dee2e6;
    border-left: none;
    border-radius: 0 0.375rem 0.375rem 0;
    background-color: #ffffff;

    &:focus {
      border-color: #80bdff;
      border-left: none;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      outline: none;
    }
  }

  // Style the select dropdown options for better spacing
  ::v-deep .input-group select option {
    padding: 0.5rem 1rem;
    margin: 0.125rem 0;
  }

  // Ensure consistent spacing for form text
  .form-text {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

  .modal-footer-section {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;

    .buttons {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;

      .btn {
        min-width: 120px;

        &.btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          color: #ffffff;

          &:hover:not(:disabled) {
            background-color: #0056b3;
            border-color: #0056b3;
          }
        }

        &.btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
          color: #ffffff;

          &:hover:not(:disabled) {
            background-color: #545b62;
            border-color: #545b62;
          }
        }
      }
    }
  }
}
</style>
